import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { NavBadgeService } from '../services/nav-badge.service';

/** A single sidebar entry — leaf level (rendered as a link).
 *  Section groupings above these entries are text-only headings
 *  driven by the top-level `SideNavGroup.label`; no group nesting
 *  below leaves. */
export interface SideNavItem {
  icon: string;
  label: string;
  route: string;
  /** Key into NavBadgeService.unseen — draws the red pip when true. */
  unseenKey?: 'candidates' | 'backfill';
}

/** A section grouping. Its `label` renders as an uppercase muted
 *  heading above the flat children list; the group carries no
 *  icon of its own and no route — it's a label, not a link. */
export interface SideNavGroup {
  label: string;
  /** Stable DOM id for the heading — precomputed at init so template
   *  bindings don't recompute a slug on every change-detection pass. */
  id: string;
  items: SideNavItem[];
}

/** Slugify a group label into a stable DOM id (module-private so the
 *  transform is co-located with the interface it feeds). */
function groupIdFor(label: string): string {
  return 'nav-group-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const STORAGE_KEY = 'kaboom.sideNav.collapsed';

@Component({
  selector: 'lib-side-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule],
  templateUrl: './side-nav.html',
  styleUrl: './side-nav.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.collapsed]': 'collapsed()',
  },
})
export class SideNavComponent {
  private readonly badgeService = inject(NavBadgeService);
  readonly unseen = this.badgeService.unseen;

  /** Persisted collapse state. Defaults to collapsed on first visit. */
  readonly collapsed = signal<boolean>(this.readStoredCollapsed());

  /**
   * Static nav definition — resolved once at class init. Section
   * headings are the top-level labels; each group's `items` array
   * holds the flat children rendered as direct links below the
   * heading. No sub-nesting.
   *
   * If this ever becomes dynamic (backend-driven, per-tenant,
   * feature-flagged, etc.) it MUST become a signal so `unseenItems`
   * (below) re-evaluates on change — a plain array reassignment
   * doesn't invalidate a downstream computed.
   */
  readonly items: SideNavGroup[] = ([
    {
      label: 'Dashboards',
      items: [
        { icon: 'pi pi-home',      label: 'Home',      route: '/dashboard' },
        { icon: 'pi pi-megaphone', label: 'Campaigns', route: '/campaigns' },
      ],
    },
    {
      label: 'Workbench',
      items: [
        { icon: 'pi pi-check-square', label: 'Tasks', route: '/tasks' },
      ],
    },
    {
      label: 'Applicants',
      items: [
        { icon: 'pi pi-users', label: 'All Applicants', route: '/candidates', unseenKey: 'candidates' },
      ],
    },
    {
      label: 'Opportunities',
      items: [
        { icon: 'pi pi-refresh', label: 'Backfill', route: '/backfill', unseenKey: 'backfill' },
      ],
    },
    {
      label: 'Jobs',
      items: [
        { icon: 'pi pi-briefcase', label: 'Job Listings', route: '/jobs' },
      ],
    },
    {
      label: 'Tools and Automation',
      items: [
        { icon: 'pi pi-sitemap',  label: 'Workflows',          route: '/workflows' },
        { icon: 'pi pi-share-alt', label: 'Pipeline Templates', route: '/pipeline-templates' },
      ],
    },
  ] satisfies Omit<SideNavGroup, 'id'>[]).map(g => ({ ...g, id: groupIdFor(g.label) }));

  /** Width the main-content padding should reserve — reactive so the shell
   *  can bind directly to keep things in step with the toggle. */
  readonly widthPx = computed(() => (this.collapsed() ? 56 : 240));

  constructor() {
    // Publish the current width to the document root as --side-nav-width so
    // the shell's .app-main can reserve matching right-padding. Kept in an
    // effect so the collapsed toggle reflows layout without extra plumbing.
    effect(() => {
      document.documentElement.style.setProperty(
        '--side-nav-width',
        `${this.widthPx()}px`,
      );
    });
    // Remove the CSS var when the component tears down so a stale value
    // doesn't outlive the sidebar (matters in tests + any conditional-mount
    // scenario the shell might introduce later).
    inject(DestroyRef).onDestroy(() =>
      document.documentElement.style.removeProperty('--side-nav-width'),
    );
  }

  toggle(): void {
    const next = !this.collapsed();
    this.collapsed.set(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Private-mode / storage-disabled — collapse state just won't persist.
    }
  }

  /**
   * Precomputed set of items currently carrying an unseen indicator.
   * Rebuilt once per unseen()-signal change and reused across
   * template renders — a plain method bound in the template would
   * re-check on every change-detection cycle. Set lookup keyed on
   * the item reference is O(1).
   */
  private readonly unseenItems = computed<Set<SideNavItem>>(() => {
    const unseen = this.unseen();
    const set = new Set<SideNavItem>();
    for (const group of this.items) {
      for (const child of group.items) {
        if (child.unseenKey && unseen[child.unseenKey]) set.add(child);
      }
    }
    return set;
  });

  isUnseen(item: SideNavItem): boolean {
    return this.unseenItems().has(item);
  }

  /**
   * Shared label source for both `aria-label` and the hover `title` on
   * each leaf link. Returns null when the sidebar is expanded — the
   * `[attr.*]` bindings then drop both attributes entirely, so no
   * tooltip or aria override can appear alongside the visible
   * `<span class="label">`. Reviewers: this guard is why the template
   * doesn't need an additional `collapsed() ? ...` gate at the call
   * site — the helper IS the gate. When collapsed, an unseen item
   * appends a "(new activity)" cue so screen-reader and hover users
   * both get the signal that sighted users see via the red dot
   * (which is aria-hidden).
   */
  collapsedAriaLabel(item: SideNavItem): string | null {
    if (!this.collapsed()) return null;
    return this.isUnseen(item) ? `${item.label} (new activity)` : item.label;
  }

  private readStoredCollapsed(): boolean {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // Default (no stored value) → collapsed.
      if (raw === null) return true;
      return raw === '1';
    } catch {
      return true;
    }
  }
}
